import { useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { authClient } from '../../services/authClient'
import { ApiClientError } from '../../services/apiClient'
import { useAuth } from '../../auth/AuthContext'
import { AuthShell } from './LoginPage'

/**
 * Paso 1 del reset: pedir el correo.
 * El backend siempre responde 200 (anti-enumeration), así que mostramos un mensaje neutro.
 */
export function PasswordResetRequestPage() {
  const { state } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [info, setInfo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (state.status === 'authenticated') {
    return <Navigate to={state.user.audience === 'admin' ? '/admin' : '/dashboard'} replace />
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const res = await authClient.requestPasswordReset({ email })
      setInfo(res.message)
      setTimeout(() => navigate('/verify-otp', { state: { email, purpose: 'password_reset' as const } }), 600)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo iniciar la recuperación.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthShell title="Recuperar contraseña" subtitle="Te enviaremos un código a tu correo">
      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <label className="field">
          <span>Correo electrónico</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            disabled={submitting}
          />
        </label>

        {error && <p className="error">{error}</p>}
        {info && <p className="info">{info}</p>}

        <button type="submit" className="primary-btn" disabled={submitting}>
          {submitting ? 'Enviando…' : 'Enviar código'}
        </button>

        <div className="auth-links">
          <Link to="/login">Volver al inicio de sesión</Link>
        </div>
      </form>
    </AuthShell>
  )
}

/**
 * Paso 3 del reset: con el reset_ticket en mano, definir la nueva contraseña.
 */
export function PasswordResetConfirmPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const state = (location.state ?? {}) as { resetTicket?: string; email?: string }
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  if (!state.resetTicket) {
    return <Navigate to="/password-reset" replace />
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setFieldErrors({})

    if (password !== passwordConfirm) {
      setError('Las contraseñas no coinciden.')
      return
    }

    setSubmitting(true)
    try {
      await authClient.confirmPasswordReset({
        reset_ticket: state.resetTicket!,
        new_password: password,
      })
      navigate('/login', { replace: true, state: { resetSuccess: true } })
    } catch (err) {
      if (err instanceof ApiClientError) {
        const fields = err.apiError.details?.fields
        if (fields && typeof fields === 'object') setFieldErrors(fields as Record<string, string>)
        setError(err.apiError.message)
      } else {
        setError('No se pudo actualizar la contraseña.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthShell title="Nueva contraseña" subtitle={state.email ? `Para ${state.email}` : undefined}>
      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <label className="field">
          <span>Nueva contraseña</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            disabled={submitting}
          />
          <small className="muted">Mínimo 8 caracteres con mayúscula, minúscula y número.</small>
          {fieldErrors.new_password && <small className="field-error">{fieldErrors.new_password}</small>}
        </label>

        <label className="field">
          <span>Confirma la contraseña</span>
          <input
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            required
            autoComplete="new-password"
            disabled={submitting}
          />
        </label>

        {error && <p className="error">{error}</p>}

        <button type="submit" className="primary-btn" disabled={submitting}>
          {submitting ? 'Actualizando…' : 'Actualizar contraseña'}
        </button>
      </form>
    </AuthShell>
  )
}
